import {
  faArrowRight,
  faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ROLES, SCOPES } from '../../../config/permissions/roles';
import { combine } from '../../../helpers/styles';
import { useJuror } from '../../../hooks/useAuth';
import { JUROR_GROUP_PHASES } from '../../../models/jurorGroup';
import PermissionHandler from '../../../modules/permissionHandler';
import { isAwardType } from '../../../helpers/awardTypes';
import { AWARD_TYPES } from '../../../models/award';
import { SESSION_TYPES } from '../../../models/session';
import { matchRoles } from '../../../helpers/roles';
import styles from './styles.module.scss';

const AverageScoreColumn = ({
  averageScore,
  averageScoreFromPriorSession,
  submission,
  setScoreModalData,
  onEntryClick,
  readOnly,
}) => {
  const { chairPersonMustDecide } = submission;
  const { t } = useTranslation();
  const { role, jurorGroup, session } = useJuror();

  const scoreAlert =
    submission?.scoreAlertA ||
    submission?.scoreAlertB ||
    submission?.scoreAlertC;
  const canSeeScoreAlert =
    role !== ROLES.juror ||
    jurorGroup.jurorGroupPhase !== JUROR_GROUP_PHASES.editPhase;

  const canClickScoreAlert = scoreAlert && !readOnly && canSeeScoreAlert;
  const canClickScoreModal =
    (isAwardType(AWARD_TYPES.IF_DA, session) &&
      role === ROLES.groupLeader &&
      session?.sessionType === SESSION_TYPES.PRESELECTION) ||
    canClickScoreAlert;

  const isSustainabilityScoreVisible =
    isAwardType(AWARD_TYPES.IF_DA, session) &&
    session?.sessionType === SESSION_TYPES.FINAL_JURY &&
    matchRoles(role, [ROLES.staff]);
  const sustainabilityScore = isSustainabilityScoreVisible
    ? submission.criteriaScores?.find(
        (criterion) => criterion.name === 'Sustainability'
      )?.finalPoints
    : null;

  const getPrevSessionSustainabilityScore = () => {
    if (!isSustainabilityScoreVisible) return null;

    const totalPoints =
      submission?.scoresFromPriorSessions?.[0]?.criteria?.find(
        (criterion) => criterion.name === 'Sustainability'
      )?.totalPoints;
    const numDecidedEvaluations =
      submission?.scoresFromPriorSessions?.[0]?.numDecidedEvaluations;

    if (
      isNaN(totalPoints) ||
      isNaN(numDecidedEvaluations) ||
      numDecidedEvaluations === 0
    )
      return null;

    const roundedScore = (totalPoints / numDecidedEvaluations).toFixed(0);

    return roundedScore;
  };

  const prevSessionSustainabilityScore = getPrevSessionSustainabilityScore();

  const handleOnScoreClick = () => {
    setScoreModalData(submission);
  };

  return (
    <div
      className={styles.averageScoreContainer}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.left}>
        <div
          className={combine(
            styles.averageScore,
            styles.currentSession,
            scoreAlert && canSeeScoreAlert && styles.scoreAlert
          )}
          role={canClickScoreModal && 'button'}
          onClick={handleOnScoreClick}
        >
          <span className={styles.value}>
            <PermissionHandler scopes={[SCOPES.canLetChairpersonDecide]}>
              {chairPersonMustDecide && (
                <span
                  className={styles.chairpersonLabel}
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'jurorEntries.table.columns.averageScore.chairperson'
                    ),
                  }}
                ></span>
              )}
            </PermissionHandler>
            <PermissionHandler scopes={[SCOPES.chairPerson, SCOPES.staff]}>
              {averageScore?.toFixed(0) || 0}
            </PermissionHandler>
            <PermissionHandler scopes={[SCOPES.juror, SCOPES.groupLeader]}>
              {!chairPersonMustDecide && (averageScore?.toFixed(0) || 0)}
            </PermissionHandler>

            <PermissionHandler scopes={[SCOPES.canSeeScoreAlert]}>
              {scoreAlert && canSeeScoreAlert && (
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  size="xs"
                  className={styles.alertIcon}
                />
              )}
            </PermissionHandler>
          </span>
          {!readOnly && (
            <span className={styles.title}>
              {t(
                `jurorEntries.table.columns.averageScore.${session?.sessionType}`
              )}
            </span>
          )}
          {isSustainabilityScoreVisible && (
            <PermissionHandler scopes={[SCOPES.staff]}>
              <span className={styles.sustainabilityScore}>
                {t(
                  'jurorEntries.table.columns.averageScore.sustainabilityScore'
                )}
                <span className={styles.sustainabilityScoreValue}>
                  {sustainabilityScore || 0}
                </span>
              </span>
            </PermissionHandler>
          )}
        </div>

        {averageScoreFromPriorSession && (
          <div className={combine(styles.averageScore, styles.priorSession)}>
            <span className={styles.value}>{averageScoreFromPriorSession}</span>
            {!readOnly && (
              <span className={styles.title}>
                {t(
                  `jurorEntries.table.columns.averageScore.${submission?.scoresFromPriorSessions?.[0]?.sessionType}`
                )}
              </span>
            )}
            {isSustainabilityScoreVisible && (
              <PermissionHandler scopes={[SCOPES.staff]}>
                <span className={styles.sustainabilityScore}>
                  {t(
                    'jurorEntries.table.columns.averageScore.sustainabilityScore'
                  )}
                  <span className={styles.sustainabilityScoreValue}>
                    {prevSessionSustainabilityScore || 0}
                  </span>
                </span>
              </PermissionHandler>
            )}
          </div>
        )}
      </div>
      {!readOnly && (
        <FontAwesomeIcon
          icon={faArrowRight}
          size="2x"
          className={combine(
            styles.arrow,
            role == ROLES.chairPerson &&
              !submission?.chairPersonMustDecide &&
              styles.blue
          )}
          onClick={onEntryClick}
        />
      )}
    </div>
  );
};

AverageScoreColumn.propTypes = {
  averageScore: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  averageScoreFromPriorSession: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  submission: PropTypes.object,
  setScoreModalData: PropTypes.func,
  onEntryClick: PropTypes.func,
  readOnly: PropTypes.bool,
};

AverageScoreColumn.defaultProps = {
  averageScore: 0,
  averageScoreFromPriorSession: null,
  submission: {},
  setScoreModalData: () => {},
  onEntryClick: () => {},
  readOnly: false,
};

export default AverageScoreColumn;
